import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTerm } from '../../contexts/TermContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  BookOpen, ClipboardList, Clock, Zap, GraduationCap, Megaphone,
  TrendingUp, Play, ChevronRight, CheckCircle2, ShoppingBag
} from 'lucide-react';
import TimetableWidget from '../../components/TimetableWidget';
import PaystackCheckout from '../../components/PaystackCheckout';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import { toast } from 'sonner';

const ADMISSION_WINDOWS = [
  { value: 'JAN', label: 'January 2026 Intake' },
  { value: 'APR', label: 'April 2026 Intake' },
  { value: 'JUL', label: 'July 2026 Intake' },
  { value: 'OCT', label: 'October 2026 Intake' },
];

const PROG_COLORS = [
  { from: '#1B2A5E', to: '#2D3F7A' },
  { from: '#1B5E20', to: '#2E7D32' },
  { from: '#4A148C', to: '#6A1B9A' },
  { from: '#C9A84C', to: '#A07C28' },
  { from: '#880E4F', to: '#C2185B' },
  { from: '#006064', to: '#00838F' },
];

const TABS = ['All Programmes', 'Enrolled', 'Available'];

export default function StudentHome() {
  const { user } = useAuth();
  const { activeTerm } = useTerm();
  const location = useLocation();
  const navigate = useNavigate();
  const [enrolledProgrammes, setEnrolledProgrammes] = useState([]);
  const [allProgrammes, setAllProgrammes] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All Programmes');

  // Enrolment flow
  const [enrolProg, setEnrolProg] = useState(null);
  const [enrolWindow, setEnrolWindow] = useState('JAN');
  const [enrolDialogOpen, setEnrolDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [enrolRes, progRes, lesRes, asgnRes, quizRes, annRes] = await Promise.all([
        api.get('/enrolments/my').catch(() => ({ data: [] })),
        api.get('/academic/programmes/public').catch(() => ({ data: [] })),
        api.get('/lms/lessons', { params: { published: true } }).catch(() => ({ data: [] })),
        api.get('/assignments').catch(() => ({ data: [] })),
        api.get('/quiz/student/available').catch(() => ({ data: [] })),
        api.get('/live/announcements').catch(() => ({ data: [] })),
      ]);
      setEnrolledProgrammes(enrolRes.data || []);
      setAllProgrammes(progRes.data || []);
      setLessons(lesRes.data || []);
      setAssignments(asgnRes.data || []);
      setQuizzes(quizRes.data || []);
      setAnnouncements(annRes.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Verify Paystack payment on return from hosted checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference') || params.get('trxref');
    if (!reference) return;
    window.history.replaceState({}, '', '/student/home');
    api.post('/enrolments/verify', { reference })
      .then(res => {
        if (res.data.status === 'success') {
          toast.success(`Enrolled in ${res.data.programme_name || 'programme'}!`);
          fetchData();
        }
      })
      .catch(() => {});
  }, [location.search, fetchData]);

  const openEnrolDialog = (prog) => {
    setEnrolProg(prog);
    setEnrolWindow('JAN');
    setEnrolDialogOpen(true);
  };

  const handleEnrolSuccess = () => {
    setEnrolDialogOpen(false);
    fetchData();
  };

  if (loading) return <HomeUniLoader />;

  const pendingAssignments = assignments.filter(a => !a.my_submission);
  const pendingQuizzes = (quizzes || []).filter(q => !q.my_submission);

  const enrolledIds = new Set(enrolledProgrammes.map(e => e.programme_id));
  const availableProgrammes = allProgrammes.filter(p => !enrolledIds.has(p.programme_id));

  const tabProgrammes = {
    'All Programmes': allProgrammes,
    'Enrolled': enrolledProgrammes.map(ep => ({ ...ep, _enrolled: true })),
    'Available': availableProgrammes,
  };

  const displayList = tabProgrammes[activeTab] || [];

  return (
    <div className="space-y-8" data-testid="student-home">
      {/* Welcome Banner */}
      <div className="relative rounded-xl overflow-hidden h-44 bg-[#1B2A5E]">
        <img src="/hu-banner-1.jpeg" alt="" className="w-full h-full object-contain object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A5E]/90 to-[#1B2A5E]/20 flex items-center px-8">
          <div>
            <p className="text-white/70 text-sm mb-1">{activeTerm} · {user?.academic_year || '2025-2026'}</p>
            <h1 className="text-2xl font-bold text-white">Welcome back, {user?.name?.split(' ')[0]}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className="bg-white/20 text-white border border-white/30">{enrolledProgrammes.length} programme{enrolledProgrammes.length !== 1 ? 's' : ''} enrolled</Badge>
              {user?.admission_window && <Badge className="bg-[#C9A84C]/80 text-white border-0">{user.admission_window} Cohort</Badge>}
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card"><div className="w-10 h-10 rounded-md bg-[#EEF0F8] flex items-center justify-center mb-2"><GraduationCap className="w-5 h-5 text-[#1B2A5E]" /></div><p className="text-2xl font-bold">{enrolledProgrammes.length}</p><p className="text-sm text-slate-500">Programmes</p></div>
        <div className="stat-card"><div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center mb-2"><ClipboardList className="w-5 h-5 text-amber-600" /></div><p className="text-2xl font-bold">{enrolledProgrammes.length > 0 ? pendingAssignments.length : 0}</p><p className="text-sm text-slate-500">Assignments Due</p></div>
        <div className="stat-card"><div className="w-10 h-10 rounded-md bg-[#EEF0F8] flex items-center justify-center mb-2"><Zap className="w-5 h-5 text-[#1B2A5E]" /></div><p className="text-2xl font-bold">{enrolledProgrammes.length > 0 ? pendingQuizzes.length : 0}</p><p className="text-sm text-slate-500">Quizzes Available</p></div>
        <div className="stat-card"><div className="w-10 h-10 rounded-md bg-[#F5EDD4] flex items-center justify-center mb-2"><TrendingUp className="w-5 h-5 text-[#C9A84C]" /></div><p className="text-2xl font-bold">{enrolledProgrammes.length > 0 ? lessons.length : 0}</p><p className="text-sm text-slate-500">Lessons</p></div>
      </div>

      {/* Announcements */}
      {announcements.length > 0 && (
        <Card className="rounded-xl border-[#1B2A5E]/20 bg-[#EEF0F8]">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#1B2A5E]" /> Announcements</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {announcements.slice(0, 3).map(a => (
                <div key={a.announcement_id} className={`p-3 rounded-lg border ${a.priority === 'urgent' ? 'bg-red-50 border-red-200' : a.priority === 'important' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-[#1B2A5E]">{a.title}</h4>
                    <span className="text-[10px] text-slate-400">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  <p className="text-xs text-slate-600">{a.body}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── PROGRAMMES ── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1B2A5E] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#C9A84C]" /> Programmes
          </h2>
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-[#1B2A5E] text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
                {tab === 'Enrolled' && enrolledProgrammes.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-[#C9A84C] text-white rounded-full px-1.5 py-0.5">{enrolledProgrammes.length}</span>
                )}
                {tab === 'Available' && availableProgrammes.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-slate-400 text-white rounded-full px-1.5 py-0.5">{availableProgrammes.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {displayList.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayList.map((prog, i) => {
              const isEnrolled = prog._enrolled || enrolledIds.has(prog.programme_id);
              const colors = PROG_COLORS[i % PROG_COLORS.length];
              return (
                <div key={prog.programme_id || prog.enrolment_id || i} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow bg-white">
                  {/* Card header */}
                  <div className="h-20 flex items-end px-5 pb-3 relative" style={{ background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)` }}>
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    {isEnrolled && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-green-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Enrolled
                      </div>
                    )}
                    {prog.code && !isEnrolled && (
                      <Badge className="absolute top-3 right-3 bg-white/20 text-white border-white/30 text-[10px]">{prog.code}</Badge>
                    )}
                  </div>
                  {/* Card body */}
                  <div className="p-4">
                    <h3 className="font-bold text-[#1B2A5E] mb-0.5 line-clamp-1">{prog.programme_name || prog.name}</h3>
                    {prog.department && <p className="text-xs text-slate-400 mb-2">{prog.department}</p>}
                    {prog.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{prog.description}</p>}
                    <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-4">
                      {prog.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {prog.duration}</span>}
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {prog.course_count || 0} courses</span>
                    </div>

                    {isEnrolled ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 rounded-lg h-8 text-xs bg-[#1B2A5E] hover:bg-[#152248] text-white"
                          onClick={() => navigate('/student/lessons')}
                        >
                          <Play className="w-3 h-3 mr-1" /> Continue
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#1B2A5E]">GHS {Number(prog.price || 0).toLocaleString()}</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-lg h-8 text-xs border-[#1B2A5E] text-[#1B2A5E] hover:bg-[#EEF0F8]"
                            onClick={() => navigate(`/programmes/${prog.programme_id}`)}
                          >
                            Details
                          </Button>
                          <Button
                            size="sm"
                            className="rounded-lg h-8 text-xs bg-[#C9A84C] hover:bg-[#A07C28] text-white border-0"
                            onClick={() => openEnrolDialog(prog)}
                          >
                            Enrol <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-400">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-sm">
              {activeTab === 'Enrolled' ? 'You have not enrolled in any programmes yet' :
               activeTab === 'Available' ? 'No new programmes to explore right now' :
               'No programmes available yet'}
            </p>
          </div>
        )}
      </section>

      {/* Timetable */}
      <TimetableWidget compact />

      {/* Assignments + Lessons — only shown once enrolled */}
      {enrolledProgrammes.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="rounded-xl border-slate-200">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" /> Assignments Due</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingAssignments.slice(0, 5).map(a => (
                  <div key={a.assignment_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0"><ClipboardList className="w-4 h-4 text-amber-600" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1B2A5E] truncate">{a.title}</p><p className="text-xs text-slate-400">{a.subject_name}</p></div>
                    {a.due_date && (
                      <span className="text-xs text-slate-400 flex items-center gap-1 whitespace-nowrap">
                        <Clock className="w-3 h-3" />
                        {new Date(a.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                ))}
                {pendingAssignments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">All caught up!</p>}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-slate-200">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#1B2A5E]" /> Recent Lessons</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {lessons.slice(0, 5).map(l => (
                  <div key={l.lesson_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-9 h-9 rounded-lg bg-[#EEF0F8] flex items-center justify-center flex-shrink-0"><BookOpen className="w-4 h-4 text-[#1B2A5E]" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1B2A5E] truncate">{l.title}</p><p className="text-xs text-slate-400">{l.subject_name}</p></div>
                    <Badge variant="secondary" className="text-xs capitalize">{l.lesson_type}</Badge>
                  </div>
                ))}
                {lessons.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No lessons available yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enrolment dialog */}
      <Dialog open={enrolDialogOpen} onOpenChange={v => { if (!v) { setEnrolDialogOpen(false); setEnrolProg(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#1B2A5E]" /> Enrol in Programme
            </DialogTitle>
          </DialogHeader>
          {enrolProg && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-[#EEF0F8] border border-[#1B2A5E]/20">
                <h3 className="font-bold text-[#1B2A5E]">{enrolProg.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{enrolProg.department} · {enrolProg.duration}</p>
                {enrolProg.description && <p className="text-sm text-slate-600 mt-2">{enrolProg.description}</p>}
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                  <span><BookOpen className="w-3 h-3 inline mr-1" />{enrolProg.course_count || 0} courses</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Select Admission Cohort</label>
                <Select value={enrolWindow} onValueChange={(v) => setEnrolWindow(v)}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Choose a cohort…" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[200]">
                    {ADMISSION_WINDOWS.map(w => (
                      <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Programme Fee</span>
                <span className="font-bold text-[#1B2A5E] text-lg">GHS {Number(enrolProg.price || 0).toLocaleString()}</span>
              </div>

              <PaystackCheckout
                programme={enrolProg}
                student={user}
                admissionWindow={enrolWindow}
                onSuccess={handleEnrolSuccess}
                onClose={() => setEnrolDialogOpen(false)}
              />

              <p className="text-[11px] text-slate-400 text-center">
                Secured payment · Bank card or Mobile Money accepted
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
